import { type Donation } from '@koala/sdk';
import { SocialCausesModal } from '@/components/account/social-causes-modal/social-causes-modal';
import { useDonateToSocialCause } from '@/components/account/social-causes-modal/social-causes-modal-hooks';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { styled } from '@/components/content/stitches';
import { Modal } from '@/components/uielements/modal';
import { StyledLinkButton } from '@/components/uielements/reward/styles';
import { useSelector } from '@/redux';
import { useWebConfig } from '@/redux/cmsConfig';
import { organizationSliceSelector } from '@/redux/organization/reducer';

interface Props {
  rewardName: string;
  donation: Donation;
}

/**
 * This component is used to render a button that opens a modal to donate a reward
 *
 * @param donation - Donation object.
 * Where "item" value is:
 * - if "type" is "currency" - $ amount that needs to be donated
 * - if "type" is "reward" - reward_id of the reward that needs to be donated.
 * - if "type" is "redeemable" - redeemable_id of the redeemable that needs to be donated.
 * @param rewardName - Name of the reward
 */
export function DonateToSocialCause({ donation, rewardName }: Props) {
  const webConfig = useWebConfig();
  const organization = useSelector(organizationSliceSelector);
  const { mutateAsync: donateToSocialCause, isLoading } = useDonateToSocialCause();
  const isDonatableRewardsEnabled =
    organization.loyalty.features?.supportsSocialCauses && webConfig.rewards.donatable_rewards;

  if (!isDonatableRewardsEnabled) {
    return null;
  }

  return (
    <Modal.Container
      trigger={(open) => (
        <Button onClick={open} aria-label={`Click to donate reward ${rewardName}`}>
          <StringAccessor accessor="rewards.donate_reward_button" />
        </Button>
      )}
    >
      {(toggle) => (
        <SocialCausesModal
          {...donation}
          toggle={toggle}
          onSubmit={async (socialCause) =>
            await donateToSocialCause({
              socialCause,
              donation,
            })
          }
          isLoading={isLoading}
        />
      )}
    </Modal.Container>
  );
}

const Button = styled(StyledLinkButton, {
  alignSelf: 'center',
  width: '100%',
});
